// client\src\pages\MyLearning\Certifications.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, IconButton, Menu, MenuItem, Divider, CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { getUserCertificates } from '../../services/certificateService';
import { useNotification } from '../../context/NotificationContext';

function Certifications() {
    const { showNotification } = useNotification();
    const [certifications, setCertifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCert, setSelectedCert] = useState(null);
    const open = Boolean(anchorEl);

    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                const response = await getUserCertificates();
                if (response.success) {
                    setCertifications(response.data || []);
                } else {
                    setError(response.message || "Failed to load certificates.");
                }
            } catch (err) {
                setError("An error occurred while fetching certificates.");
            } finally {
                setLoading(false);
            }
        };
        fetchCertificates();
    }, []);

    const handleMenuOpen = (event, certId) => {
        setAnchorEl(event.currentTarget);
        setSelectedCert(certId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCert(null);
    };

    const handleCopyLink = (cert) => {
        navigator.clipboard.writeText(`${window.location.origin}/certificate/view/${cert.csnId}`)
            .then(() => showNotification("Certificate link has been copied", "info"))
            .catch((err) => console.error("Failed to copy link", err));
        handleMenuClose();
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (certifications.length === 0) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography variant="h6">No Certificates Available</Typography>
                <Typography variant="body2" color="textSecondary">
                    Once you complete a course, your certificates will appear here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {certifications.map((cert) => (
                <Card
                    key={cert._id} // Using _id as the unique key
                    sx={{
                        marginBottom: '1rem',
                        padding: '0.4rem',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                        },
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    <CardContent>
                        <IconButton
                            onClick={(e) => handleMenuOpen(e, cert._id)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                            }}
                        >
                            <MoreVertIcon />
                        </IconButton>

                        <Menu
                            anchorEl={anchorEl}
                            open={open && selectedCert === cert._id}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem onClick={() => handleCopyLink(cert)}>
                                Copy Certificate Link
                            </MenuItem>
                        </Menu>
                        <Typography
                            variant="h6"
                            sx={{
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                fontSize: '1.1rem',
                            }}
                        >
                            {cert.course.title} {/* Fetching course title from API response */}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                marginBottom: '1rem',
                                color: 'text.secondary',
                            }}
                        >
                            Issued on: {new Date(cert.issuedAt).toLocaleDateString()} {/* Formatting issued date */}
                        </Typography>

                        <Divider sx={{ marginBottom: '1rem' }} />

                        <Box
                            sx={{
                                display: 'flex',
                                gap: '1rem',
                                flexWrap: 'wrap',
                                '@media (max-width: 600px)': {
                                    flexDirection: 'column',
                                },
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<LinkedInIcon />}
                                sx={{
                                    textTransform: 'none',
                                    flex: 1,
                                }}
                            >
                                Share on LinkedIn
                            </Button>

                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    textTransform: 'none',
                                    flex: 1,
                                }}
                            >
                                Download Certificate
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}

export default Certifications;
